import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import Home from '../views/Home.vue'
import Forms from '../views/Forms.vue'
import Domains from '../views/Domains.vue'
import Contact from '../views/Contact.vue'
import Payment from '../views/Payment.vue'
import Submissions from '../views/Submissions.vue'
import Custom from '../views/Custom.vue'
import Filler from '../views/Filler.vue'
import FillerBeta from '../views/FillerBeta.vue'
import FillerBetaMapper from '../views/FillerBetaMapper.vue'
import FillerBetaDesigner from '../views/FillerBetaDesigner.vue'
import HelpIndex from '../views/HelpIndex.vue'
import HelpFormFillerSetup from '../views/HelpFormFillerSetup.vue'
import HelpPdfDesigner from '../views/HelpPdfDesigner.vue'
import HelpDomains from '../views/HelpDomains.vue'
import Settings from '../views/Settings.vue'
import MyAccount from '../views/MyAccount.vue'
import Admin from '../views/Admin.vue'
import Login from '../views/Login.vue'
import AcceptInvite from '../views/AcceptInvite.vue'
import ResetPassword from '../views/ResetPassword.vue'
import store from '../store'

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/forms',
    name: 'Forms',
    component: Forms
  },
  {
    path: '/domains',
    name: 'Domains',
    component: Domains,
    meta: { requiresAccountManager: true }
  },
  {
    path: '/submissions/:formId',
    name: 'Submissions',
    component: Submissions
  },
  {
    path: '/payment',
    name: 'Payment',
    component: Payment,
    meta: { requiresAccountManager: true }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact
  },
  {
    path: '/custom',
    name: 'Custom Module',
    component: Custom
  },
  {
    path: '/filler',
    name: 'Form Filler',
    component: Filler
  },
  {
    path: '/filler-beta',
    name: 'Form Filler Beta',
    component: FillerBeta
  },
  {
    path: '/filler-beta/design/:name',
    name: 'Form Filler Beta Designer',
    component: FillerBetaDesigner
  },
  {
    path: '/filler-beta/:name',
    name: 'Form Filler Beta Mapper',
    component: FillerBetaMapper
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    meta: { requiresAccountManager: true }
  },
  {
    path: '/help',
    name: 'Help',
    component: HelpIndex
  },
  {
    path: '/help/form-filler-setup',
    name: 'Help Form Filler Setup',
    component: HelpFormFillerSetup
  },
  {
    path: '/help/pdf-designer',
    name: 'Help PDF Designer',
    component: HelpPdfDesigner
  },
  {
    path: '/help/domains',
    name: 'Help Domains',
    component: HelpDomains
  },
  {
    path: '/my-account',
    name: 'MyAccount',
    component: MyAccount
  },
  {
    path: '/admin',
    name: 'Admin',
    component: Admin
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/accept-invite/:token',
    name: 'AcceptInvite',
    component: AcceptInvite
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: ResetPassword
  },
]

const router = new VueRouter({
  base: process.env.BASE_URL,
  routes
})

// Settings/Domains/Payment/Team are Account Manager (or System Admin) only -
// a General User can use the product but not touch account config/billing/team.
// Everything else relies on the backend gate (this is just to avoid flashing
// a page that will immediately fail its own data fetch).
router.beforeEach(async (to, from, next) => {
  if(!to.matched.some(record => record.meta.requiresAccountManager)) {
    next();
    return;
  }

  // Wait for the most recently started session check (App.vue.checkSession)
  // to finish before deciding, not just the first one - sessionChecked flips
  // true once and never resets, so on its own it only protects the initial
  // page-load race (same issue already hit once in FillerBeta.vue's tier
  // check). Without awaiting the LATEST check specifically, a decision made
  // right after switching accounts could run on stale role data from before
  // that switch's own session refresh landed.
  const pending = (store as any)._sessionCheckPromise;
  if(pending) {
    await pending;
  } else if(!store.state.sessionChecked) {
    await new Promise(resolve => {
      const unwatch = store.watch(state => state.sessionChecked, checked => {
        if(checked) {
          unwatch();
          resolve(undefined);
        }
      });
    });
  }

  if(store.state.isSystemAdmin || store.state.accountRole === 'AccountManager') {
    next();
  } else {
    next('/');
  }
});

export default router
